import { Fragment } from "react";
import { Eye, Settings2, CornerDownRight } from "lucide-react";
import { BLOG_MANAGEMENT_HEAD_OFFICE_ID, PANEL_CATALOG, type PanelDef, type PanelLevel } from "../config/panelCatalog";
import type { PanelPermission } from "../api/access";
import { sessionGet } from "../utils/sessionStore";

export function PermissionGridEditor({
  permissions,
  setVisible,
  setLevel,
  panelCatalog,
  editableKeys,
}: {
  permissions: Record<string, PanelPermission>;
  setVisible: (key: string, visible: boolean) => void;
  setLevel: (key: string, level: PanelLevel) => void;
  panelCatalog?: PanelDef[];
  editableKeys?: string[];
}) {
  const catalog = (panelCatalog || PANEL_CATALOG).filter(
    (panel) =>
      panel.key !== "set-blog-management" ||
      sessionGet("headOfficeID") === BLOG_MANAGEMENT_HEAD_OFFICE_ID
  );
  const groups = catalog.reduce<string[]>((result, panel) => {
    if (!result.includes(panel.group)) result.push(panel.group);
    return result;
  }, []);
  // When set, only these panel keys are interactive; every other row renders
  // read-only. Used for manager-role users, who may flip only the Lio toggle —
  // the hrms backend narrows their saves to that cell regardless.
  const canEdit = (key: string) => !editableKeys || editableKeys.includes(key);

  return (
    <div className="am-anim am-card bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="grid grid-cols-[1fr_84px_168px] items-center px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        <span>Section</span>
        <span className="text-center">Visible</span>
        <span className="text-center">Level</span>
      </div>

      {groups.map((group) => (
        <div key={group} className="mb-4 last:mb-0">
          <div className="flex items-center gap-2 px-1 py-1.5">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
              {group}
            </span>
            <span className="flex-1 h-px bg-gray-100" />
          </div>
          <div className="rounded-xl border border-gray-100 overflow-hidden">
            {catalog.filter((p) => p.group === group && !p.feature).map((panel, i) => {
              const perm = permissions[panel.key];
              const children = catalog.filter((f) => f.feature && f.parent === panel.key);
              const showChildren = children.length > 0 && perm.visible && perm.level === "manage";
              return (
                <Fragment key={panel.key}>
                  <div
                    className={`am-prow grid grid-cols-[1fr_84px_168px] items-center px-3 py-2.5 ${
                      perm.visible ? "bg-emerald-50/30" : i % 2 ? "bg-gray-50/40" : "bg-white"
                    } hover:bg-emerald-50/40`}
                  >
                    <span className="text-sm font-medium text-gray-800">{panel.label}</span>
                    <div className="flex justify-center">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={perm.visible}
                        aria-label={`Toggle ${panel.label}`}
                        disabled={!canEdit(panel.key)}
                        onClick={() => setVisible(panel.key, !perm.visible)}
                        className={`relative inline-flex h-[22px] w-10 items-center rounded-full transition-colors ${
                          perm.visible ? "bg-emerald-500" : "bg-gray-300"
                        } ${canEdit(panel.key) ? "" : "opacity-40 cursor-not-allowed"}`}
                      >
                        <span
                          className={`inline-block h-[18px] w-[18px] transform rounded-full bg-white shadow-sm transition-transform ${
                            perm.visible ? "translate-x-[20px]" : "translate-x-[2px]"
                          }`}
                        />
                      </button>
                    </div>
                    <div className="flex justify-center">
                      <div
                        className={`inline-flex rounded-lg border p-0.5 bg-gray-50 ${
                          perm.visible && canEdit(panel.key)
                            ? "border-gray-200"
                            : "border-gray-100 opacity-40 pointer-events-none"
                        }`}
                      >
                        {(["view", "manage"] as PanelLevel[]).map((lvl) => (
                          <button
                            key={lvl}
                            type="button"
                            onClick={() => setLevel(panel.key, lvl)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                              perm.level === lvl ? "bg-emerald-500 text-white shadow-sm" : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            {lvl === "view" ? <Eye className="w-3 h-3" /> : <Settings2 className="w-3 h-3" />}
                            {lvl === "view" ? "View" : "Manage"}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {showChildren &&
                    children.map((child) => {
                      const cperm = permissions[child.key];
                      return (
                        <div
                          key={child.key}
                          className="grid grid-cols-[1fr_84px_168px] items-center pl-8 pr-3 py-2 bg-emerald-50/10 border-t border-dashed border-emerald-100"
                        >
                          <span className="text-sm text-gray-600 flex items-center gap-2">
                            <CornerDownRight className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                            {child.label}
                          </span>
                          <div className="flex justify-center">
                            <button
                              type="button"
                              role="switch"
                              aria-checked={cperm.visible}
                              aria-label={`Toggle ${child.label}`}
                              disabled={!canEdit(child.key)}
                              onClick={() => setVisible(child.key, !cperm.visible)}
                              className={`relative inline-flex h-[20px] w-9 items-center rounded-full transition-colors ${
                                cperm.visible ? "bg-emerald-500" : "bg-gray-300"
                              } ${canEdit(child.key) ? "" : "opacity-40 cursor-not-allowed"}`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                                  cperm.visible ? "translate-x-[18px]" : "translate-x-[2px]"
                                }`}
                              />
                            </button>
                          </div>
                          <div className="flex justify-center">
                            {child.leveled ? (
                              <div
                                className={`inline-flex rounded-lg border p-0.5 bg-gray-50 ${
                                  cperm.visible && canEdit(child.key)
                                    ? "border-gray-200"
                                    : "border-gray-100 opacity-40 pointer-events-none"
                                }`}
                              >
                                {(["view", "manage"] as PanelLevel[]).map((lvl) => (
                                  <button
                                    key={lvl}
                                    type="button"
                                    onClick={() => setLevel(child.key, lvl)}
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-md transition-colors ${
                                      cperm.level === lvl
                                        ? "bg-emerald-500 text-white shadow-sm"
                                        : "text-gray-500 hover:text-gray-700"
                                    }`}
                                  >
                                    {lvl === "view" ? <Eye className="w-3 h-3" /> : <Settings2 className="w-3 h-3" />}
                                    {lvl === "view" ? "View" : "Manage"}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <span className="text-[11px] font-medium text-gray-400">
                                {cperm.visible ? "Allowed" : "Hidden"}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </Fragment>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
